import React, { useRef } from 'react';
import moment from 'moment';
import { FormControlLabel } from '@material-ui/core';
import cx from 'classnames';
import { CountryCode } from 'libphonenumber-js';

import CustomRadio from './CustomRadio/CustomRadio';
import styles from './PatientCard.module.scss';
import { NULL_PATIENT_ID, NewPatient } from '../Review/Review';
import { capitalizeAvatarName, concatFullName } from 'utils/common';
import {
  formatPhoneNumberWithCountryCode,
  geCountryByPhoneNumber,
} from 'utils/formatPhoneNumber';
import useIsLoadedInsideIframe from 'hooks/useIsLoadedInsideIframe';
import { InsuranceCoverage } from 'interfaces/insuranceType';
import { ILastHygieneAppt } from 'interfaces/familyBookingTypes';

interface IPatient extends NewPatient {
  id: string;
  homeAddress?: string;
  insuranceCoverages?: InsuranceCoverage[];
  lastHygieneAppt?: ILastHygieneAppt | null;
}

interface PatientCardProps {
  isMultipleResult?: boolean;
  patient: IPatient;
  chosenPatientId: string;
  selectedInsurance?: {
    insuranceCarrierName: string;
    insuranceCarrierId: string;
  } | null;
}

const userInfo = [
  { label: 'Date of birth', property: 'dob' },
  { label: 'Email', property: 'email' },
  { label: 'Phone number', property: 'phoneNumber' },
  { label: 'Home address', property: 'homeAddress' },
  { label: 'Insurance', property: 'insuranceCoverages' },
  { label: 'Last cleaning', property: 'lastHygieneAppt' },
];

const PatientCard = (props: PatientCardProps) => {
  const {
    isMultipleResult = false,
    patient,
    chosenPatientId,
    selectedInsurance,
  } = props;
  const isLoadedInsideIframe = useIsLoadedInsideIframe();

  const radioInputRef = useRef<HTMLLabelElement>();

  // Suggest another patient if patient has the same info but different dob
  const isPatientIdNullIncluded =
    !patient.id && chosenPatientId === NULL_PATIENT_ID;

  const containerClassName = cx({
    [styles['container']]: true,
    [styles['active']]:
      isMultipleResult &&
      (chosenPatientId === patient.id || isPatientIdNullIncluded),
    [styles['hover-cursor']]:
      isMultipleResult &&
      (chosenPatientId !== patient.id || isPatientIdNullIncluded),
  });

  const leftSectionClassName = cx({
    [styles['left-section']]: true,
    [styles['left-section-iframe']]: isLoadedInsideIframe,
  });

  const avatarText = capitalizeAvatarName(
    `${patient.firstName.replace(/\s/g, '').trim()} ${patient.lastName
      .replace(/\s/g, '')
      .trim()}`
  );

  const renderText = (userInfo: { label: string; property: string }) => {
    const property = userInfo.property as keyof IPatient;
    let value = patient[property];

    if (property === 'dob') {
      value = moment(patient[property]).format('MMM DD, YYYY');
    }

    if (property === 'phoneNumber') {
      const countryCode = geCountryByPhoneNumber(patient[property]);
      value = countryCode
        ? formatPhoneNumberWithCountryCode(
            patient[property],
            countryCode as CountryCode
          )
        : patient[property];
    }

    if (property === 'insuranceCoverages') {
      const latestInsurance =
        patient.insuranceCoverages?.[0]?.insuranceCarrierName;

      value = selectedInsurance
        ? selectedInsurance.insuranceCarrierName
        : latestInsurance || '';
    }

    if (property === 'lastHygieneAppt') {
      value = moment(patient.lastHygieneAppt?.startDate, 'YYYY-MM-DD').format(
        'MMM DD, YYYY'
      );
    }

    return value;
  };

  const handleOnClickContainer = () => {
    if (!isMultipleResult) return;
    radioInputRef.current?.click();
  };

  return (
    <div className={containerClassName} onClick={handleOnClickContainer}>
      {isMultipleResult && (
        <FormControlLabel
          ref={radioInputRef}
          label=""
          control={<CustomRadio />}
          className={styles['control-radio']}
          value={patient.id || NULL_PATIENT_ID}
        />
      )}
      <div className={leftSectionClassName}>
        <div className={styles['avatar']}>{avatarText}</div>
        <div className={styles['full-name']}>
          {concatFullName(patient.firstName, patient.lastName)}
        </div>
      </div>
      <div className={styles['right-section']}>
        {userInfo.map((userInfo) => {
          const property = userInfo.property as keyof IPatient;

          const isLastHygieneApptMissing =
            property === 'lastHygieneAppt' &&
            !patient.lastHygieneAppt?.startDate;

          if (!patient[property] || isLastHygieneApptMissing) {
            return null;
          }

          return (
            <div key={userInfo.label} className={styles['user-info']}>
              <span className={styles['user-detail']}>{userInfo.label}:</span>
              <span className={styles['user-data']}>
                {renderText(userInfo)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PatientCard;
